/**
 * @file UserMessageList.js
 * @description Displays user messages/inbox with sorting, filtering, and preview functionality
 */

import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    getNotificationMessages,
    getNotificationCategories,
    getNotificationChannels,
} from '../../../services/messageService';
import TextRangeDatePicker from '../../helpers/TextRangeDatePicker';
import { FilterModal } from '../../helpers/FilterComponent';
import NotFoundBox from '../../ui/no_data_indicators/NotFoundBox';
import Spinner from '../../ui/spinners/Spinner';
import Toast from '../../ui/toasts/Toast';
import PaginationComponent from '../../helpers/PaginationComponent';
import UserMessagePreview from './UserMessagePreview';
import styles from './UserMessageList.module.scss';
import { parseISO, max } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

import { ReactComponent as MessageIcon } from '../../../utils/icons/Icon_SMS.svg';
import { ReactComponent as EmailIcon } from '../../../utils/icons/Icon_Email.svg';
import { ReactComponent as PushIcon } from '../../../utils/icons/Icon_Push.svg';
import RichTextContent from '../../ui/RichTextContent';
import { formatPhoneNumber } from '../../../utils/utils';
import { useAuthContext } from '../../../store/Contexts';
import { getText } from '../../../i18n';
import { ROUTES, ROUTE_BUILDERS } from '../../../constants/routes';

export const MESSAGE_STATUS = {
    ACTIVE: 1,
    OPTED_OUT: 2,
};

const SortDirections = {
    ASCENDING: 'asc',
    DESCENDING: 'desc',
};

const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};

function UserMessageList() {
    const pageSize = 10;
    const isUserOnMobileDevice = window.innerWidth <= 786;
    const { menuLabels, menuLabelsLoaded } = useAuthContext();
    const { notificationId } = useParams();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [messages, setMessages] = useState([]);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [categories, setCategories] = useState([]);
    const [channels, setChannels] = useState([]);
    const [startDate, setStartDate] = useState(
        new Date(Date.now() - 90 * 24 * 60 * 60 * 1000),
    );
    const [endDate, setEndDate] = useState(new Date());
    const [searchInput, setSearchInput] = useState('');
    const debouncedSearch = useDebounce(searchInput, 500);
    const [sortConfig, setSortConfig] = useState({
        key: 'sentDate',
        direction: SortDirections.DESCENDING,
    });
    const [activeModal, setActiveModal] = useState(null);
    const [selections, setSelections] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const messagesMenuLabel = menuLabelsLoaded ? menuLabels.messages : '';

    const navigateToMessage = message => {
        const messagePath = ROUTE_BUILDERS.myMessagesView(
            message.notificationId,
        );
        navigate(messagePath, { replace: pathname === messagePath });
    };

    const handleMessageClick = message => {
        setSelectedMessage(message);
        navigateToMessage(message);
    };

    const closeDetailView = () => {
        navigate(ROUTES.MY_MESSAGES);
    };

    useEffect(() => {
        if (!notificationId) {
            setIsDetailOpen(false);
            return;
        }

        const routedMessage = messages.find(
            message => String(message.notificationId) === notificationId,
        );
        if (!routedMessage) {
            return;
        }

        setSelectedMessage(currentMessage =>
            String(currentMessage?.notificationId) === notificationId
                ? currentMessage
                : routedMessage,
        );
        setIsDetailOpen(true);
    }, [notificationId, messages]);

    useEffect(() => {
        loadInitialData();
    }, []);

    useEffect(() => {
        loadMessages();
    }, [
        currentPage,
        sortConfig,
        startDate,
        endDate,
        debouncedSearch,
        selections,
    ]);

    const getChannelIcon = channelName => {
        switch (channelName.toLowerCase()) {
            case 'email':
                return EmailIcon;
            case 'sms':
                return MessageIcon;
            case 'push':
                return PushIcon;
            default:
                return null;
        }
    };

    const loadInitialData = async () => {
        try {
            setIsLoading(true);
            const [categoriesResponse, channelsResponse] = await Promise.all([
                getNotificationCategories(),
                getNotificationChannels(),
            ]);

            const channelsWithIcons = channelsResponse.map(channel => ({
                ...channel,
                icon: getChannelIcon(channel.name),
            }));

            setCategories(categoriesResponse);
            setChannels(channelsWithIcons);
            await loadMessages();
        } catch (error) {
            console.error('Error loading initial data:', error);
            Toast.error(getText('msg_0afa4f1e45b5'));
        } finally {
            setIsLoading(false);
        }
    };
    const loadMessages = async () => {
        try {
            setIsLoading(true);
            const userTimeZone =
                Intl.DateTimeFormat().resolvedOptions().timeZone;

            const response = await getNotificationMessages({
                search: debouncedSearch,
                sort:
                    sortConfig.direction === SortDirections.ASCENDING
                        ? 'sentDateAsc'
                        : 'sentDateDesc',
                categoryIds: selections.category || [],
                channelIds: selections.channels || [],
                sentDateStart: startDate,
                sentDateEnd: endDate,
                timeZone: userTimeZone,
                page: currentPage,
                pageSize,
            });

            const formattedMessages = response.notificationMessages.map(msg => {
                const plainTextBody = msg.plainTextBody || '';

                return {
                    ...msg,
                    sentDate: formatInTimeZone(
                        parseISO(msg.sentDate),
                        userTimeZone,
                        'MMM dd yyyy hh:mm a zzz',
                    ),
                    category: categories.find(
                        cat => cat.id === msg.notificationCategoryId,
                    ),
                    preview:
                        plainTextBody.substring(0, 50) +
                        (plainTextBody.length > 50 ? '...' : ''),
                    sentDateUTC: msg.sentDate,
                };
            });

            const groupedMessages = formattedMessages.reduce((acc, msg) => {
                const scheduleId = msg.notificationScheduleId;
                if (!acc[scheduleId]) {
                    acc[scheduleId] = [];
                }
                acc[scheduleId].push(msg);
                return acc;
            }, {});

            const formattedGroupedMessages = Object.values(groupedMessages).map(
                messageGroup => {
                    const firstMessage = messageGroup[0];

                    const messages = messageGroup.map(msg => ({
                        id: msg.id,
                        notificationMessageChannelId:
                            msg.notificationMessageChannelId,
                        sentDateFormatted: formatInTimeZone(
                            msg.sentDateUTC,
                            userTimeZone,
                            'MMM dd yyyy hh:mm a zzz',
                        ),
                        sentDate: msg.sentDateUTC,
                        destination:
                            msg.notificationMessageChannelId == 2
                                ? formatPhoneNumber(msg.destination)
                                : msg.destination,
                    }));

                    const notificationChannelIds =
                        getNotificationChannelArray(messages);
                    const latestSentDateObj = max(
                        messages.map(m => m.sentDate),
                    );
                    const lastSentDateFormatted = formatInTimeZone(
                        latestSentDateObj,
                        userTimeZone,
                        'MMM dd yyyy hh:mm a zzz',
                    );

                    return {
                        id: firstMessage.notificationScheduleId,
                        notificationId: firstMessage.notificationId,
                        subject: firstMessage.subject,
                        preview: firstMessage.preview,
                        htmlBody: firstMessage.htmlBody,
                        notificationCategoryId:
                            firstMessage.notificationCategoryId,
                        messages,
                        latestSentDate: lastSentDateFormatted,
                        latestSentDateObj,
                        notificationChannelIds,
                    };
                },
            );

            // Explicitly sort by latestSentDateObj descending (newest first)
            formattedGroupedMessages.sort(
                (a, b) => b.latestSentDateObj - a.latestSentDateObj,
            );

            formattedGroupedMessages.forEach(msg => {
                delete msg.latestSentDateObj;
            });

            setMessages(formattedGroupedMessages);
            setTotalPages(Math.ceil(response.recordCount / pageSize));

            if (formattedGroupedMessages.length > 0 && !selectedMessage) {
                setSelectedMessage(formattedGroupedMessages[0]);
            }
        } catch (error) {
            console.error('Failed to fetch messages', error);
            Toast.error(getText('msg_a7552826f97c'));
            setMessages([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSort = key => {
        setSortConfig(prevConfig => ({
            key,
            direction:
                prevConfig.key === key &&
                prevConfig.direction === SortDirections.ASCENDING
                    ? SortDirections.DESCENDING
                    : SortDirections.ASCENDING,
        }));
    };

    const handleSearch = event => {
        setSearchInput(event.target.value);
    };

    const openModal = column => {
        setActiveModal(column);
    };

    const closeModal = () => {
        setActiveModal(null);
    };

    const getOptions = column => {
        switch (column) {
            case 'category':
                return categories.map(cat => cat.name);
            case 'channels':
                return channels.map(ch => ch.name);
            case 'subject':
                return Array.from(new Set(messages.map(msg => msg.subject)))
                    .filter(Boolean)
                    .sort();
            default:
                return [];
        }
    };

    const handleApply = (column, selectedOptions) => {
        setActiveModal(null);

        switch (column) {
            case 'category': {
                const selectedCategoryIds = categories
                    .filter(cat => selectedOptions.includes(cat.name))
                    .map(cat => cat.id);
                setSelections(prev => ({
                    ...prev,
                    [column]: selectedCategoryIds,
                }));
                break;
            }
            case 'channels': {
                const selectedChannelIds = channels
                    .filter(ch => selectedOptions.includes(ch.name))
                    .map(ch => ch.id);
                setSelections(prev => ({
                    ...prev,
                    [column]: selectedChannelIds,
                }));
                break;
            }
            case 'subject':
                setSelections(prev => ({ ...prev, [column]: selectedOptions }));
                break;
            default:
                break;
        }
    };

    const clearFilters = () => {
        setSelections({});
        setSearchInput('');
        setCurrentPage(1);
    };

    const renderSortArrow = (key, isMobile = false) => {
        if (sortConfig.key !== key) return null;
        return (
            <span className={`mx-2 ${!isMobile ? styles.sortArrowIcon : ''}`}>
                <svg width="11" height="6" viewBox="0 0 11 6" fill="none">
                    <path
                        d={
                            sortConfig.direction === SortDirections.ASCENDING
                                ? 'M1 5.25L5.25 0.75L9.5 5.25'
                                : 'M1 0.75L5.25 5.25L9.5 0.75'
                        }
                        stroke="#2F2B43"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </span>
        );
    };

    const renderChannelIcon = channelId => {
        const channel = channels.find(ch => ch.id === channelId);
        if (!channel || !channel.icon) return null;
        const IconComponent = channel.icon;
        return <IconComponent />;
    };

    const getCategoryName = categoryId => {
        const category = categories.find(cat => cat.id === categoryId);
        return category ? category.name : '';
    };

    const getNotificationChannelArray = messages => {
        const notificationMessageChannelIds = messages.map(
            msg => msg.notificationMessageChannelId,
        );
        const distinctNotificationMessageChannelIds = [
            ...new Set(notificationMessageChannelIds),
        ];
        return distinctNotificationMessageChannelIds.sort();
    };

    return (
        <>
            {isUserOnMobileDevice && (
                <div
                    className={`${styles.messageDetailView} ${isDetailOpen ? styles.open : ''}`}
                >
                    <div className={styles.messageDetailContent}>
                        <div className={styles.messageDetailHeader}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="15"
                                height="25"
                                viewBox="0 0 15 25"
                                fill="none"
                                onClick={closeDetailView}
                            >
                                <path
                                    d="M12.5 25L0 12.5L12.5 0L14.7188 2.21875L4.4375 12.5L14.7188 22.7813L12.5 25Z"
                                    fill="#2F2B43"
                                />
                            </svg>
                            <div className={styles.messageDetailMessagesButton}>
                                {messagesMenuLabel}
                            </div>
                        </div>
                        <div className={styles.messageDetailDetails}>
                            <h1>{selectedMessage?.subject}</h1>
                            <div className={styles.messageDetailDate}>
                                {selectedMessage?.latestSentDate}
                            </div>
                            <div
                                className={`container ${styles.messageDetailSentTo}`}
                            >
                                {selectedMessage?.messages.map((msg, index) => (
                                    <div className="row">
                                        <div className="col-2">
                                            {index === 0 ? (
                                                <b>
                                                    {getText(
                                                        'msg_c8a76cfeccfd',
                                                    )}
                                                </b>
                                            ) : (
                                                ''
                                            )}
                                        </div>
                                        <div className="col-10">
                                            <span
                                                className={`${styles.channelIcon} text-success`}
                                            >
                                                {renderChannelIcon(
                                                    msg.notificationMessageChannelId,
                                                )}
                                            </span>{' '}
                                            {msg.notificationMessageChannelId !==
                                            3
                                                ? msg.destination || ''
                                                : 'Push Notification'}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className={styles.messageDetailBody}>
                            <RichTextContent
                                content={selectedMessage?.htmlBody || ''}
                            />
                        </div>
                    </div>
                </div>
            )}
            <div className={`${styles.pageContentBody} page-content`}>
                <div className={`${styles.cardBody} card`}>
                    <div className="card-body px-0 py-4">
                        <div className={styles.msgHeader}>
                            <p
                                className={`page-title card-title ${styles.title}`}
                            >
                                {messagesMenuLabel}
                            </p>
                            <div className={styles.search}>
                                <input
                                    type="text"
                                    className={styles.searchInput}
                                    placeholder={getText('msg_0915d68e34f3')}
                                    value={searchInput}
                                    onChange={handleSearch}
                                />
                                <span className={styles.searchIcon}>
                                    <i className="bi bi-search"></i>
                                </span>
                            </div>
                            <div
                                className={styles.sent}
                                onClick={() => {
                                    handleSort('sentDate');
                                }}
                            >
                                <div>{getText('msg_35f49dcfbfb2')}</div>
                                {renderSortArrow(
                                    'sentDate',
                                    isUserOnMobileDevice,
                                )}
                            </div>
                            <span
                                type="button"
                                onClick={clearFilters}
                                className={`ing-text-buttons mx-3 ${styles.clearFilters}`}
                            >
                                <i className="bi bi-arrow-counterclockwise me-2" />
                                {getText('msg_381c4bf1d409')}
                            </span>
                            <div className={styles.dateRange}>
                                <TextRangeDatePicker
                                    startDate={startDate}
                                    endDate={endDate}
                                    setStartDate={setStartDate}
                                    setEndDate={setEndDate}
                                />
                            </div>
                        </div>
                        {isLoading ? (
                            <Spinner
                                width={50}
                                className={styles.loadingSpinner}
                            />
                        ) : messages.length === 0 ? (
                            <NotFoundBox
                                backgroundColor="#f0f4ff"
                                title={getText('msg_40b50d4ffbb3')}
                                description={getText('msg_8726865e061f')}
                            />
                        ) : !isUserOnMobileDevice ? (
                            <div className={styles.messageContainer}>
                                <table className="table table-hover align-middle ing-table-striped">
                                    <thead className="custom-table-header table-light">
                                        <tr>
                                            <th
                                                style={{ cursor: 'pointer' }}
                                                className="position-relative"
                                                onClick={() =>
                                                    handleSort('subject')
                                                }
                                            >
                                                <span className="table-column-header-text">
                                                    {getText(
                                                        'msg_8d183dbdcea3',
                                                    )}{' '}
                                                    {renderSortArrow('subject')}
                                                </span>
                                            </th>
                                            <th>
                                                {getText('msg_68f4145fee7d')}
                                            </th>
                                            <th
                                                style={{ cursor: 'pointer' }}
                                                className="position-relative"
                                                onClick={() =>
                                                    handleSort('category')
                                                }
                                            >
                                                <span className="table-column-header-text">
                                                    {getText(
                                                        'msg_a3c686e711e4',
                                                    )}{' '}
                                                    {renderSortArrow(
                                                        'category',
                                                    )}
                                                </span>
                                                <span
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        openModal('category');
                                                    }}
                                                    className={`${styles.cursorPointer} pe-3`}
                                                >
                                                    <i
                                                        className="bi bi-funnel"
                                                        style={{
                                                            fontSize: '0.7em',
                                                        }}
                                                    ></i>
                                                </span>
                                                {activeModal === 'category' && (
                                                    <FilterModal
                                                        column={activeModal}
                                                        options={getOptions(
                                                            activeModal,
                                                        )}
                                                        selectedOptions={
                                                            selections[
                                                                activeModal
                                                            ]?.map(id =>
                                                                getCategoryName(
                                                                    id,
                                                                ),
                                                            ) || []
                                                        }
                                                        onApply={handleApply}
                                                        onClose={closeModal}
                                                    />
                                                )}
                                            </th>
                                            <th className="position-relative">
                                                <span className="table-column-header-text">
                                                    {getText(
                                                        'msg_879f0b1bef59',
                                                    )}
                                                </span>
                                                <span
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        openModal('channels');
                                                    }}
                                                    className={`${styles.cursorPointer} ms-2 pe-3`}
                                                >
                                                    <i
                                                        className="bi bi-funnel"
                                                        style={{
                                                            fontSize: '0.7em',
                                                        }}
                                                    ></i>
                                                </span>
                                                {activeModal === 'channels' && (
                                                    <FilterModal
                                                        column={activeModal}
                                                        options={getOptions(
                                                            activeModal,
                                                        )}
                                                        selectedOptions={
                                                            selections[
                                                                activeModal
                                                            ]?.map(
                                                                id =>
                                                                    channels.find(
                                                                        ch =>
                                                                            ch.id ===
                                                                            id,
                                                                    )?.name,
                                                            ) || []
                                                        }
                                                        onApply={handleApply}
                                                        onClose={closeModal}
                                                    />
                                                )}
                                            </th>
                                            <th
                                                style={{ cursor: 'pointer' }}
                                                onClick={() =>
                                                    handleSort('sentDate')
                                                }
                                            >
                                                <span className="table-column-header-text">
                                                    {getText(
                                                        'msg_35f49dcfbfb2',
                                                    )}{' '}
                                                    {renderSortArrow(
                                                        'sentDate',
                                                    )}
                                                </span>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {messages.map(message => (
                                            <tr
                                                key={message.id}
                                                onClick={() =>
                                                    handleMessageClick(message)
                                                }
                                                className={
                                                    selectedMessage?.id ===
                                                    message.id
                                                        ? styles.selected
                                                        : ''
                                                }
                                            >
                                                <td className="titleCellMessages">
                                                    {message.subject ||
                                                        '[No Subject]'}
                                                </td>
                                                <td>{message.preview}</td>
                                                <td>
                                                    {getCategoryName(
                                                        message.notificationCategoryId,
                                                    )}
                                                </td>
                                                <td>
                                                    <div
                                                        className={`${styles.channelIcons} text-success`}
                                                    >
                                                        {message.notificationChannelIds.map(
                                                            channelId =>
                                                                renderChannelIcon(
                                                                    channelId,
                                                                ),
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    {message.latestSentDate}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                {totalPages > 0 && (
                                    <div className="mt-0 d-flex justify-content-center">
                                        <PaginationComponent
                                            currentPage={currentPage}
                                            totalPages={totalPages}
                                            onPageChange={setCurrentPage}
                                        />
                                    </div>
                                )}

                                {selectedMessage && (
                                    <UserMessagePreview
                                        message={selectedMessage}
                                        renderChannelIcon={renderChannelIcon}
                                    />
                                )}
                            </div>
                        ) : (
                            <div
                                className={styles.mobileMessagesTableContainer}
                            >
                                {messages.map((message, index) => (
                                    <div
                                        key={message.id}
                                        onClick={() =>
                                            handleMessageClick(message)
                                        }
                                    >
                                        <div
                                            className={
                                                styles.mobileMessagesHeader
                                            }
                                        >
                                            <div
                                                className={
                                                    styles.mobileMessageSubject
                                                }
                                            >
                                                {message.subject}
                                            </div>
                                            <div
                                                className={
                                                    styles.mobileMessageDate
                                                }
                                            >
                                                {message.lastSentDate}
                                            </div>
                                        </div>
                                        <div
                                            className={
                                                styles.mobileMessagePreview
                                            }
                                        >
                                            {message.preview}
                                        </div>
                                        {index < messages.length - 1 && (
                                            <div
                                                className={
                                                    styles.mobileMessageDivider
                                                }
                                            ></div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default UserMessageList;
